import type { useNotificationReadSession } from '@/hooks/queries/useNotificationReadSession'
import type { QueryClient } from '@tanstack/react-query'
import { executeNotificationReader } from '@/hooks/queries/notification-reader-cache'
import { queryKeys } from '@/hooks/query-keys'
import { notificationReadsApi } from '@/modules/notifications/reads.api'
import { hashKey } from '@tanstack/react-query'

import type { Notification } from '@scrambl/shared'

const deliveries = new WeakMap<
  QueryClient,
  Map<string, { promise: Promise<void>; pending: boolean }>
>()
const admitted = new WeakMap<object, () => void>()

/** Consumers recheck this lease immediately before native/companion delivery. */
export function isOwnedNotificationDeliveryCurrent(
  notification: unknown,
): notification is Notification {
  if (!notification || typeof notification !== 'object') return false
  const assertCurrent = admitted.get(notification)
  if (!assertCurrent) return false
  try {
    assertCurrent()
    return true
  } catch {
    return false
  }
}

/** Coalesce mounted consumers; retain bounded identity metadata, never bodies. */
export function deliverOwnedNotificationHint(
  client: QueryClient,
  session: ReturnType<typeof useNotificationReadSession>,
  hint: unknown,
  dispatch: (notification: Notification) => void,
): Promise<void> {
  try {
    session.assertCurrent()
  } catch {
    return Promise.resolve()
  }
  if (!hint || typeof hint !== 'object') return Promise.resolve()
  const notification = (hint as { notification?: unknown }).notification
  if (!notification || typeof notification !== 'object')
    return Promise.resolve()
  const row = notification as Partial<Notification>
  // Never use event text, counts or routes. An ID only selects a fresh reader.
  if (
    typeof row.id !== 'string' ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      row.id,
    )
  )
    return Promise.resolve()
  if (
    row.recipientId !== session.ownerUserId ||
    row.organizationId !== session.organizationId
  )
    return Promise.resolve()
  const timestamp = (value: unknown) => {
    if (
      !(value instanceof Date) &&
      (typeof value !== 'string' || value.length > 64)
    )
      return undefined
    const time = new Date(value).getTime()
    return Number.isFinite(time) ? time : undefined
  }
  const version =
    timestamp(row.lastMessageAt) ??
    timestamp(row.updatedAt) ??
    timestamp(row.createdAt) ??
    0
  const identity = hashKey([session.key, row.id, version])
  let entries = deliveries.get(client)
  if (!entries) {
    entries = new Map()
    deliveries.set(client, entries)
  }
  const existing = entries.get(identity)
  if (existing) return existing.promise
  // Do not evict pending jobs: their repeated hints must remain coalesced.
  if (entries.size >= 500) {
    for (const [key, entry] of entries) {
      if (!entry.pending) entries.delete(key)
      if (entries.size < 500) break
    }
    if (entries.size >= 500) return Promise.resolve()
  }
  const owned = entries
  const id = row.id
  const key = [...queryKeys.notifications.detail(id), session.key]
  const job = Promise.resolve().then(async () => {
    try {
      session.assertCurrent()
      const result = await client.fetchQuery({
        queryKey: key,
        queryFn: ({ signal }) =>
          executeNotificationReader(client, key, session, signal, context =>
            notificationReadsApi.detail(id, context),
          ),
        staleTime: 0,
        gcTime: 60000,
        retry: false,
      })
      session.assertCurrent()
      admitted.set(result, session.assertCurrent)
      dispatch(result)
    } catch {
      owned.delete(
        identity,
      ) /* No event-body fallback and no automatic delivery retry. */
    }
  })
  const entry = { promise: job, pending: true }
  owned.set(identity, entry)
  void job.then(() => {
    entry.pending = false
  })
  return job
}
